import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clapperboard, ExternalLink, Paperclip, Upload } from "lucide-react";
import { useAuth } from "../../services/AuthContext";
import {
  createAdminAttachment,
  getAdminLesson,
  getAttachmentUploadUrl,
  updateAdminLesson,
  uploadAttachmentToS3,
} from "../../services/admin";
import AdminReloadOverlay from "./AdminReloadOverlay";

const BUNNY_LIBRARY_ID = "236258";

const emptyForm = {
  title: "",
  description: "",
  video_id: "",
  length: "",
};

function LessonPreview({ title, description, videoId }) {
  const videoSrc = videoId
    ? `https://iframe.mediadelivery.net/embed/${BUNNY_LIBRARY_ID}/${videoId}?autoplay=false&loop=false&muted=false&preload=true&responsive=true`
    : "";

  return (
    <div className="admin-lesson-preview">
      <p className="admin-control-label">Prévia da aula</p>
      <div className="lesson-main-card admin-lesson-preview-card">
        <header className="lesson-header">
          <h2 className="lesson-page-title">{title || "Aula"}</h2>
        </header>

        <div className="lesson-video">
          <div className="video-wrapper">
            {videoSrc ? (
              <div className="watch-container">
                <iframe
                  src={videoSrc}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen
                  title={title || "Vídeo da aula"}
                />
              </div>
            ) : (
              <div className="admin-lesson-preview-empty">Sem vídeo</div>
            )}
          </div>
        </div>

        {description ? (
          <div
            className="lesson-description"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        ) : (
          <p className="admin-muted">Sem descrição.</p>
        )}
      </div>
    </div>
  );
}

export default function AdminLessonPage() {
  const { courseCode, sectionCode, lessonCode } = useParams();
  const { getIdToken } = useAuth();
  const [payload, setPayload] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [attachmentError, setAttachmentError] = useState("");
  const [attachmentSuccess, setAttachmentSuccess] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const token = await getIdToken();
        const data = await getAdminLesson(token, courseCode, sectionCode, lessonCode);
        if (cancelled) return;
        setPayload(data);
        setForm({
          title: data.lesson?.title || "",
          description: data.lesson?.description || "",
          video_id: data.lesson?.video_id || "",
          length: data.lesson?.length ?? "",
        });
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Não foi possível carregar a aula.");
          setPayload(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [courseCode, getIdToken, lessonCode, sectionCode]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const lengthValue = form.length === "" ? undefined : Number(form.length);
    if (lengthValue !== undefined && (!Number.isInteger(lengthValue) || lengthValue < 0)) {
      setError("A duração deve ser um número inteiro de segundos.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      const token = await getIdToken();
      const updated = await updateAdminLesson(token, courseCode, sectionCode, lessonCode, {
        title: form.title,
        description: form.description,
        video_id: form.video_id,
        length: lengthValue,
      });
      setPayload(updated);
      setSuccess("Aula atualizada com sucesso.");
    } catch (err) {
      setError(err.message || "Não foi possível salvar a aula.");
    } finally {
      setSaving(false);
    }
  };

  const handleAttachmentUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !payload?.lesson?.id) return;

    try {
      setUploading(true);
      setAttachmentError("");
      setAttachmentSuccess("");
      const token = await getIdToken();
      const { uploadUrl, fileUrl } = await getAttachmentUploadUrl(token, {
        fileName: file.name,
        fileType: file.type || "application/octet-stream",
        courseId: courseCode,
        sectionId: sectionCode,
        lessonId: lessonCode,
      });
      await uploadAttachmentToS3(uploadUrl, file);
      await createAdminAttachment(token, {
        lessonId: payload.lesson.id,
        title: file.name,
        link: fileUrl,
      });
      const refreshed = await getAdminLesson(token, courseCode, sectionCode, lessonCode);
      setPayload(refreshed);
      setAttachmentSuccess("Anexo enviado com sucesso.");
    } catch (err) {
      setAttachmentError(err.message || "Não foi possível enviar o anexo.");
    } finally {
      setUploading(false);
    }
  };

  if (!payload?.lesson) {
    return (
      <div className="admin-page admin-page-narrow">
        <AdminReloadOverlay visible={loading} message="Atualizando aula..." />
        {!loading && (
          <>
            <Link to={`/admin/content/${courseCode}`} className="admin-back">
              <ArrowLeft size={16} /> Voltar ao curso
            </Link>
            <div className="admin-error">{error || "Aula não encontrada."}</div>
          </>
        )}
      </div>
    );
  }

  const lesson = payload.lesson;
  const attachments = lesson.attachments || [];

  return (
    <div className="admin-page admin-page-narrow">
      <AdminReloadOverlay
        visible={loading || saving || uploading}
        message="Atualizando aula..."
      />
      <Link to={`/admin/content/${courseCode}`} className="admin-back">
        <ArrowLeft size={16} /> Voltar ao curso
      </Link>
      <div className="admin-page-header admin-page-header-split">
        <div>
          <h1 className="admin-title">
            <Clapperboard size={30} className="admin-title-icon" />
            {lesson.title || lesson.code}
          </h1>
          <p className="admin-subtitle">
            {payload.sectionTitle || sectionCode} · {lesson.code}
          </p>
        </div>
        <a
          className="admin-secondary-btn"
          href={`/courses/${courseCode}/sections/${sectionCode}/lessons/${lesson.code}`}
          target="_blank"
          rel="noreferrer"
          title="Ver aula"
        >
          <ExternalLink size={16} />
          Ver aula
        </a>
      </div>

      <LessonPreview
        title={form.title}
        description={form.description}
        videoId={form.video_id.trim()}
      />

      {error && <div className="admin-error">{error}</div>}
      {success && <div className="admin-success">{success}</div>}

      <div className="admin-panel">
        <h2 className="admin-panel-title">Editar aula</h2>
        <form onSubmit={handleSave}>
          <div className="admin-form-grid">
            <div className="admin-control admin-span-2">
              <label className="admin-control-label" htmlFor="lesson-title">Título</label>
              <input id="lesson-title" className="admin-control-input" value={form.title} onChange={handleChange("title")} />
            </div>
            <div className="admin-control">
              <label className="admin-control-label" htmlFor="lesson-video">ID do vídeo (Bunny)</label>
              <input id="lesson-video" className="admin-control-input" value={form.video_id} onChange={handleChange("video_id")} />
            </div>
            <div className="admin-control">
              <label className="admin-control-label" htmlFor="lesson-length">Duração (segundos)</label>
              <input
                id="lesson-length"
                type="number"
                min="0"
                step="1"
                className="admin-control-input"
                value={form.length}
                onChange={handleChange("length")}
              />
            </div>
            <div className="admin-control admin-span-2">
              <label className="admin-control-label" htmlFor="lesson-description">Descrição</label>
              <textarea id="lesson-description" className="admin-control-textarea" value={form.description} onChange={handleChange("description")} />
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <button type="submit" className="admin-primary-btn" disabled={saving}>
              {saving ? "Salvando..." : "Salvar aula"}
            </button>
          </div>
        </form>
      </div>

      <div className="admin-panel">
        <div className="admin-panel-title-row">
          <h2 className="admin-panel-title">Materiais de apoio</h2>
          <button
            type="button"
            className="admin-primary-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Upload size={16} />
            {uploading ? "Enviando..." : "Enviar anexo"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleAttachmentUpload}
          />
        </div>
        {attachmentError && <div className="admin-error">{attachmentError}</div>}
        {attachmentSuccess && <div className="admin-success">{attachmentSuccess}</div>}
        {attachments.length === 0 ? (
          <p className="admin-muted">Nenhum anexo nesta aula.</p>
        ) : (
          <ul className="admin-lesson-list">
            {attachments.map((file) => (
              <li key={file.id} className="admin-attachment-row">
                <span className="admin-attachment-name">
                  <Paperclip size={16} />
                  {file.title || "Arquivo anexo"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
